import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getFCMToken } from '../../../lib/firebase';
import { usePasswordRegisterMutation } from '../../../features/api/apiSlice';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../../../features/auth/authSlice';
import {
  CUSTOMER_ROLE,
  PORTAL,
  assertPortalAccess,
  buildAuthUserPayload,
} from '../../../lib/authPortals';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const LOCATION_OPTIONS = [
  { value: 'kampala', label: 'Kampala' },
  { value: 'wakiso', label: 'Wakiso' },
  { value: 'entebbe', label: 'Entebbe' },
  { value: 'mukono', label: 'Mukono' },
  { value: 'jinja', label: 'Jinja' },
  { value: 'mbarara', label: 'Mbarara' },
  { value: 'gulu', label: 'Gulu' },
  { value: 'other', label: 'Other / East Africa' },
];

const PROPERTY_TYPE_OPTIONS = [
  { value: 'house', label: 'House' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'land', label: 'Land / Plot' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'hostel', label: 'Hostel' },
  { value: 'hotel', label: 'Hotel' },
];

/** User-only registration (property seekers). */
const RegistrationForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [passwordRegister] = usePasswordRegisterMutation();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    interestedLocations: [],
    propertyTypes: [],
    agreeToTerms: false,
    subscribeNewsletter: true,
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return strength;
  };

  const getPasswordStrengthLabel = (strength) => {
    if (strength <= 1) return { label: 'Weak', color: 'bg-error' };
    if (strength <= 3) return { label: 'Medium', color: 'bg-warning' };
    return { label: 'Strong', color: 'bg-success' };
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s\-()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'password') setPasswordStrength(calculatePasswordStrength(value));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const displayName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();

      let fcmToken = '';
      try {
        fcmToken = (await getFCMToken()) || '';
      } catch {
        fcmToken = '';
      }

      const response = await passwordRegister({
        name: displayName,
        email: formData.email.trim(),
        password: formData.password,
        mobile: formData.phone.trim(),
        fcm_id: fcmToken,
        portal: 'customer',
        account_role: CUSTOMER_ROLE,
        address: Array.isArray(formData.interestedLocations)
          ? formData.interestedLocations.join(', ')
          : '',
      }).unwrap();

      if (response?.error === true) {
        throw new Error(response?.message || 'Registration failed');
      }

      const apiUser = response.user || response.data || {};
      const authUser = buildAuthUserPayload({
        apiUser: { ...apiUser, account_role: CUSTOMER_ROLE },
        fallback: { name: displayName, email: formData.email.trim() },
      });

      const access = assertPortalAccess(authUser.account_role, PORTAL.CUSTOMER);

      dispatch(
        setCredentials({
          user: authUser,
          token: response.token,
          customerId: response.customerId || apiUser.id,
        })
      );

      navigate(access.redirect || '/user-dashboard', {
        state: {
          message: 'Welcome! Browse listings or post what you need on the notice board.',
        },
      });
    } catch (error) {
      console.error('Registration failed:', error);
      let message = 'Registration failed. Please try again.';
      if (error?.data?.message) {
        message = error.data.message;
      } else if (error?.message) {
        message = error.message;
      }
      setErrors({ submit: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordStrengthInfo = getPasswordStrengthLabel(passwordStrength);

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="mb-6 rounded-lg border border-primary/20 bg-primary/5 p-4">
        <p className="text-sm font-medium text-foreground flex items-center gap-2">
          <Icon name="Search" size={16} className="text-primary" />
          User account
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          For people looking to buy, rent, or invest. Agents, brokers, and advisors should use{' '}
          <Link to="/pro/register" className="text-primary font-medium underline">
            Real Estate Agent registration
          </Link>
          .
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Basic Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="First Name"
              type="text"
              placeholder="Enter your first name"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              error={errors.firstName}
              required
            />
            <Input
              label="Last Name"
              type="text"
              placeholder="Enter your last name"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              error={errors.lastName}
              required
            />
          </div>
          <Input
            label="Email Address"
            type="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={errors.email}
            required
          />
          <Input
            label="Phone Number"
            type="tel"
            placeholder="+256 7XX XXX XXX"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            error={errors.phone}
            required
          />
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Account Security</h3>
          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              error={errors.password}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
            {formData.password && (
              <div className="mt-2 flex items-center space-x-2">
                <div className="flex-1 bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${passwordStrengthInfo.color}`}
                    style={{ width: `${(passwordStrength / 5) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-medium">{passwordStrengthInfo.label}</span>
              </div>
            )}
          </div>
          <div className="relative">
            <Input
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              error={errors.confirmPassword}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
            >
              <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">What you&apos;re looking for</h3>
          <Select
            label="Interested Locations"
            placeholder="Select locations"
            options={LOCATION_OPTIONS}
            value={formData.interestedLocations}
            onChange={(value) => handleInputChange('interestedLocations', value)}
            multiple
            searchable
          />
          <Select
            label="Property Types"
            placeholder="Select property types"
            options={PROPERTY_TYPE_OPTIONS}
            value={formData.propertyTypes}
            onChange={(value) => handleInputChange('propertyTypes', value)}
            multiple
          />
        </div>

        <div className="space-y-4">
          <Checkbox
            label="I agree to the Terms of Service and Privacy Policy"
            checked={formData.agreeToTerms}
            onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
            error={errors.agreeToTerms}
            required
          />
          <Checkbox
            label="Subscribe to property updates and market insights"
            checked={formData.subscribeNewsletter}
            onChange={(e) => handleInputChange('subscribeNewsletter', e.target.checked)}
          />
        </div>

        {errors.submit && (
          <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
            <p className="text-sm text-error">{errors.submit}</p>
          </div>
        )}

        <Button type="submit" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting}>
          {isSubmitting ? 'Creating Account...' : 'Create User Account'}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          Agent, broker, or advisor?{' '}
          <Link to="/pro/register" className="text-primary font-medium hover:underline">
            Register as a Real Estate Agent
          </Link>
        </p>
      </form>
    </div>
  );
};

export default RegistrationForm;
